import {
  addDoc,
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { deleteObject, getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import { auth, db, functions, storage } from "../firebase";
import type {
  CatalogueEntity,
  Customer,
  HomeContent,
  Product,
  Promotion,
  StoreConfig,
} from "../types";
import { slugify } from "../utils";

const safeFileName = (value: string) => value
  .normalize("NFKD")
  .replace(/[^a-zA-Z0-9._-]+/g, "-")
  .replace(/-+/g, "-");

export async function uploadMedia(
  folder: string,
  file: File,
  onProgress?: (progress: number) => void,
) {
  const path = `${folder}/${Date.now()}-${crypto.randomUUID()}-${safeFileName(file.name)}`;
  const target = ref(storage, path);
  await new Promise<void>((resolve, reject) => {
    const task = uploadBytesResumable(target, file, {
      contentType: file.type || "application/octet-stream",
      customMetadata: { uploadedBy: auth.currentUser?.uid ?? "unknown" },
    });
    task.on("state_changed", (snapshot) => {
      onProgress?.(Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100));
    }, reject, resolve);
  });
  return { path, url: await getDownloadURL(target) };
}

export async function deleteMedia(path?: string) {
  if (!path || /^https?:\/\//i.test(path)) return;
  await deleteObject(ref(storage, path));
}

export async function saveProduct(
  product: Partial<Product>,
  id?: string,
  variantProductIds: string[] = [],
  removedVariantProductIds: string[] = [],
) {
  const reference = id
    ? doc(db, "Products", id)
    : doc(collection(db, "Products"));
  const linkedIds = [...new Set(variantProductIds.filter((productId) => productId && productId !== reference.id))];
  const groupId = linkedIds.length > 0
    ? product.colorVariantGroupId?.trim() || `color-${crypto.randomUUID()}`
    : "";
  const payload = {
    ...product,
    schemaVersion: 3,
    id: reference.id,
    colorVariantGroupId: groupId,
    imageId: product.imageIds?.[0] ?? product.imageId ?? "",
    imageIds: product.imageIds ?? [],
    additional_photos: product.imageIds?.slice(1) ?? [],
    updatedAt: serverTimestamp(),
    ...(id ? {} : { createdAt: serverTimestamp() }),
  };
  const batch = writeBatch(db);
  batch.set(reference, payload, { merge: true });
  linkedIds.forEach((productId) => batch.update(doc(db, "Products", productId), {
    colorVariantGroupId: groupId,
    updatedAt: serverTimestamp(),
  }));
  [...new Set(removedVariantProductIds)]
    .filter((productId) => productId && productId !== reference.id && !linkedIds.includes(productId))
    .forEach((productId) => batch.update(doc(db, "Products", productId), {
      colorVariantGroupId: "",
      updatedAt: serverTimestamp(),
    }));
  await batch.commit();
  return reference.id;
}

export async function archiveProduct(id: string, archived: boolean) {
  await updateDoc(doc(db, "Products", id), {
    archived,
    active: !archived,
    updatedAt: serverTimestamp(),
  });
}

export async function permanentlyDeleteProduct(id: string) {
  const callable = httpsCallable<{ productId: string }, { deleted: boolean }>(functions, "deleteProduct");
  await callable({ productId: id });
}

export async function changeOrderStatus(orderId: string, status: number, note: string) {
  await updateDoc(doc(db, "Orders", orderId), {
    orderStatus: status,
    updatedAt: serverTimestamp(),
    statusHistory: arrayUnion({
      status,
      timestamp: new Date(),
      note: note.trim(),
      changedBy: auth.currentUser?.email ?? auth.currentUser?.uid ?? "admin",
    }),
  });
}

export async function saveCustomerAdminData(customer: Customer) {
  await updateDoc(doc(db, "User_Data", customer.id), {
    adminNotes: customer.adminNotes ?? "",
    customerStatus: customer.customerStatus ?? "active",
    tags: customer.tags ?? [],
    adminUpdatedAt: serverTimestamp(),
  });
}

export async function saveCatalogueEntity(
  collectionName: "Categories" | "Brands",
  entity: Partial<CatalogueEntity>,
  id?: string,
) {
  const reference = id
    ? doc(db, collectionName, id)
    : doc(db, collectionName, slugify(entity.name ?? ""));
  await setDoc(reference, {
    name: entity.name?.trim(),
    slug: entity.slug?.trim() || slugify(entity.name ?? ""),
    description: entity.description?.trim() ?? "",
    imageId: entity.imageId ?? "",
    active: entity.active ?? true,
    sortOrder: Number(entity.sortOrder ?? 0),
    updatedAt: serverTimestamp(),
    ...(id ? {} : { createdAt: serverTimestamp() }),
  }, { merge: true });
  return reference.id;
}

export async function removeCatalogueEntity(collectionName: "Categories" | "Brands", id: string) {
  await deleteDoc(doc(db, collectionName, id));
}

export async function renameCatalogueReferences(
  collectionName: "Categories" | "Brands",
  entityId: string,
  newName: string,
  productIds: string[],
) {
  const idField = collectionName === "Categories" ? "categoryId" : "brandId";
  const nameField = collectionName === "Categories" ? "category" : "brand";
  for (let start = 0; start < productIds.length; start += 450) {
    const batch = writeBatch(db);
    productIds.slice(start, start + 450).forEach((productId) => batch.update(doc(db, "Products", productId), {
      [idField]: entityId,
      [nameField]: newName,
      updatedAt: serverTimestamp(),
    }));
    await batch.commit();
  }
}

export async function savePromotion(promotion: Partial<Promotion>, id?: string) {
  const reference = id ? doc(db, "Promotion", id) : doc(collection(db, "Promotion"));
  const percent = Math.max(0, Math.min(100, Number(promotion.discountPercent ?? promotion.Percent ?? 0)));
  const timestampValue = (value: Promotion["startsAt"]) => {
    if (!value) return null;
    if (typeof value === "object" && "toDate" in value) return value;
    const date = new Date(value as string | number | Date);
    return Number.isNaN(date.getTime()) ? null : Timestamp.fromDate(date);
  };
  await setDoc(reference, {
    ...promotion,
    name: promotion.name?.trim() || promotion.Heading?.trim() || "Промоция",
    Heading: promotion.Heading?.trim() ?? "",
    Body: promotion.Body?.trim() ?? "",
    Percent: `${percent}%`,
    discountPercent: percent,
    targetIds: promotion.targetIds ?? [],
    startsAt: timestampValue(promotion.startsAt),
    endsAt: timestampValue(promotion.endsAt),
    updatedAt: serverTimestamp(),
    ...(id ? {} : { createdAt: serverTimestamp() }),
  }, { merge: true });
  return reference.id;
}

export async function removePromotion(id: string) {
  await deleteDoc(doc(db, "Promotion", id));
}

export async function publishPromotionNotification(id: string) {
  const callable = httpsCallable<{ promotionId: string }, { sent: boolean }>(functions, "sendPromotionNotification");
  return (await callable({ promotionId: id })).data;
}

export async function saveHomeContent(content: HomeContent) {
  await setDoc(doc(db, "App_Config", "home"), {
    ...content,
    id: "home",
    updatedAt: serverTimestamp(),
  }, { merge: true });
}

export async function saveStoreConfig(config: StoreConfig) {
  await setDoc(doc(db, "App_Config", "store"), {
    ...config,
    id: "store",
    updatedAt: serverTimestamp(),
  }, { merge: true });
}

export async function bootstrapAdmin() {
  const callable = httpsCallable<void, { granted: boolean }>(functions, "bootstrapAdmin");
  return (await callable()).data;
}

export async function setAdminUser(email: string, role: string, active: boolean) {
  const callable = httpsCallable<{ email: string; role: string; active: boolean }, { uid: string; created: boolean }>(functions, "setAdminUser");
  return (await callable({ email: email.trim(), role, active })).data;
}

export async function createSimpleEntity(collectionName: string, data: Record<string, unknown>) {
  return addDoc(collection(db, collectionName), { ...data, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
}
